export type BillingInterval = "monthly" | "annual";
export type StripeCatalogEnv = "test" | "live";
export type PlanAudience = "customer" | "supplier";

export interface BillingPlanRecord {
  id: string;
  name: string;
  slug: string;
  supplier_limit: number;
  monthly_price_pennies: number;
  annual_price_pennies: number;
  stripe_price_id_monthly: string | null;
  stripe_price_id_annual: string | null;
  stripe_product_id_monthly: string | null;
  stripe_product_id_annual: string | null;
  stripe_test_price_id_monthly?: string | null;
  stripe_test_price_id_annual?: string | null;
  stripe_test_product_id_monthly?: string | null;
  stripe_test_product_id_annual?: string | null;
  stripe_live_price_id_monthly?: string | null;
  stripe_live_price_id_annual?: string | null;
  stripe_live_product_id_monthly?: string | null;
  stripe_live_product_id_annual?: string | null;
  is_active: boolean;
  display_order: number;
  agreement_term_months?: number | null;
  plan_audience?: PlanAudience | null;
  includes_supplier_plus?: boolean | null;
}

export interface BillingPlanView {
  id: string;
  name: string;
  slug: string;
  supplierLimit: number;
  monthlyPricePennies: number;
  annualPricePennies: number;
  isActive: boolean;
  displayOrder: number;
  planAudience: PlanAudience;
  includesSupplierPlus: boolean;
}

export interface ResolvedBillingPlan {
  slug: string;
  interval: BillingInterval;
}

export function getAnnualSavingsPercent(plan: BillingPlanView): number | null {
  if (plan.monthlyPricePennies <= 0 || plan.annualPricePennies <= 0) {
    return null;
  }

  const monthlyYearlyCost = plan.monthlyPricePennies * 12;
  if (plan.annualPricePennies >= monthlyYearlyCost) {
    return null;
  }

  return Math.round((1 - (plan.annualPricePennies / monthlyYearlyCost)) * 100);
}

export function getAnnualSavingsBadgeLabel(plans: BillingPlanView[]): string | null {
  const savings = plans
    .map(getAnnualSavingsPercent)
    .filter((value): value is number => typeof value === "number" && value > 0);

  if (savings.length === 0) {
    return null;
  }

  const uniqueSavings = Array.from(new Set(savings));
  if (uniqueSavings.length === 1) {
    return `Save ${uniqueSavings[0]}%`;
  }

  return `Save up to ${Math.max(...uniqueSavings)}%`;
}

export function toBillingPlanView(plan: BillingPlanRecord): BillingPlanView {
  return {
    id: plan.id,
    name: plan.name,
    slug: plan.slug,
    supplierLimit: plan.supplier_limit,
    monthlyPricePennies: plan.monthly_price_pennies,
    annualPricePennies: plan.annual_price_pennies,
    isActive: plan.is_active,
    displayOrder: plan.display_order,
    planAudience: plan.plan_audience ?? "customer",
    includesSupplierPlus: plan.includes_supplier_plus ?? false,
  };
}

export function getBillingPlanAmount(
  plan: BillingPlanView,
  interval: BillingInterval,
): number {
  return interval === "annual"
    ? plan.annualPricePennies
    : plan.monthlyPricePennies;
}

export function getStripePriceIdForInterval(
  plan: BillingPlanRecord,
  interval: BillingInterval,
  catalogEnv: StripeCatalogEnv = "test",
): string | null {
  if (catalogEnv === "live") {
    return interval === "annual"
      ? plan.stripe_live_price_id_annual ?? plan.stripe_price_id_annual
      : plan.stripe_live_price_id_monthly ?? plan.stripe_price_id_monthly;
  }

  return interval === "annual"
    ? plan.stripe_test_price_id_annual ?? null
    : plan.stripe_test_price_id_monthly ?? null;
}

export function getStripeProductIdForInterval(
  plan: BillingPlanRecord,
  interval: BillingInterval,
  catalogEnv: StripeCatalogEnv = "test",
): string | null {
  if (catalogEnv === "live") {
    return interval === "annual"
      ? plan.stripe_live_product_id_annual ?? plan.stripe_product_id_annual
      : plan.stripe_live_product_id_monthly ?? plan.stripe_product_id_monthly;
  }

  return interval === "annual"
    ? plan.stripe_test_product_id_annual ?? null
    : plan.stripe_test_product_id_monthly ?? null;
}

export function resolveBillingPlanFromStripeIds(
  plans: BillingPlanRecord[],
  priceId: string,
  productId: string,
  catalogEnv: StripeCatalogEnv = "test",
): ResolvedBillingPlan | null {
  for (const plan of plans) {
    if (getStripePriceIdForInterval(plan, "monthly", catalogEnv) === priceId) {
      return { slug: plan.slug, interval: "monthly" };
    }
    if (getStripePriceIdForInterval(plan, "annual", catalogEnv) === priceId) {
      return { slug: plan.slug, interval: "annual" };
    }
  }

  for (const plan of plans) {
    if (
      getStripeProductIdForInterval(plan, "monthly", catalogEnv) === productId
    ) {
      return { slug: plan.slug, interval: "monthly" };
    }
    if (
      getStripeProductIdForInterval(plan, "annual", catalogEnv) === productId
    ) {
      return { slug: plan.slug, interval: "annual" };
    }
  }

  return null;
}

export function getStripeCatalogEnv(
  rawValue: string | null | undefined,
): StripeCatalogEnv {
  if (!rawValue) {
    return "test";
  }

  if (rawValue === "test" || rawValue === "live") {
    return rawValue;
  }

  throw new Error(
    `Invalid STRIPE_CATALOG_ENV value: ${rawValue}. Expected "test" or "live".`,
  );
}
